import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import InputGroup from "react-bootstrap/esm/InputGroup";

const MONTH_NAMES_DE = [
    "", "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember"
];

const MONTH_NAMES_EN = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

interface FilterControlsProps {
    currentYear: number;
    yearOptions: number[];
    currentMonth: number | null;
    availableMonths: number[];
    searchTerm: string;
    onYearChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onMonthChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onSearchChange: (value: string) => void;
    onExpandAll: () => void;
    onCollapseAll: () => void;
}

export default function FilterControls({
    currentYear,
    yearOptions,
    currentMonth,
    availableMonths,
    searchTerm,
    onYearChange,
    onMonthChange,
    onSearchChange,
    onExpandAll,
    onCollapseAll,
}: FilterControlsProps) {
    const { t, i18n } = useTranslation();
    const monthNames = i18n.language === "de" ? MONTH_NAMES_DE : MONTH_NAMES_EN;

    const getTranslationString = (translation: unknown): string =>
        typeof translation === "string" ? translation : "";

    return (
        <Card className="mb-4 shadow-sm border-0">
            <Card.Body className="py-3">
                <Row className="align-items-end g-3">
                    <Col xs={6} md={3} lg={2}>
                        <Form.Label className="small text-muted mb-1">{t("admin.yearFilter")}</Form.Label>
                        <Form.Select
                            value={currentYear}
                            onChange={onYearChange}
                            className="shadow-sm"
                            style={{ fontWeight: 500 }}
                        >
                            {yearOptions.map((year) => (
                                <option key={year} value={year}>
                                    📅 {year}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={3} lg={2}>
                        <Form.Label className="small text-muted mb-1">{t("admin.monthFilter")}</Form.Label>
                        <Form.Select
                            value={currentMonth ?? ""}
                            onChange={onMonthChange}
                            className="shadow-sm"
                            style={{ fontWeight: 500 }}
                        >
                            <option value="">{t("admin.allMonths")}</option>
                            {availableMonths.map((month) => (
                                <option key={month} value={month}>
                                    {monthNames[month]}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={12} md={4} lg={5}>
                        <Form.Label className="small text-muted mb-1">{t("common.search")}</Form.Label>
                        <InputGroup className="shadow-sm">
                            <InputGroup.Text style={{ backgroundColor: "var(--gl-surface)" }}>🔍</InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder={`${t("choirSong.songName")} ${getTranslationString(t("common.search")).toLowerCase()}...`}
                                value={searchTerm}
                                onChange={(e) => onSearchChange(e.target.value)}
                            />
                            {searchTerm && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => onSearchChange("")}
                                    title={getTranslationString(t("common.cancel"))}
                                >
                                    ✕
                                </Button>
                            )}
                        </InputGroup>
                    </Col>
                    <Col xs={12} md={2} lg={3}>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={onExpandAll}
                                className="flex-grow-1"
                                title={getTranslationString(t("admin.expandAll"))}
                            >
                                ▼ {t("admin.expandAllShort")}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={onCollapseAll}
                                className="flex-grow-1"
                                title={getTranslationString(t("admin.collapseAll"))}
                            >
                                ▲ {t("admin.collapseAllShort")}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}
